import React from 'react';
import { Link } from 'react-router-dom';
import { flattenToAppURL } from '@plone/volto/helpers';

/**
 * Values that mean "no date" but are not empty in the JavaScript sense, and so
 * would render as visible nonsense in a table cell.
 *
 * Plone answers an unset publishing date two different ways depending on which
 * catalog column is asked. The `effective` index returns the epoch sentinel
 * `1969-12-31T00:00:00+00:00`; the `EffectiveDate` metadata column returns the
 * STRING `'None'`. Neither is null, so both used to survive isEmptyValue and
 * reach fmtDate, which rendered them as `Dec 31, 1969` and a literal `None`
 * respectively. Both are worse than a blank cell: they assert something false.
 *
 * `expires`/`ExpirationDate` carry the mirror-image sentinel (`2499-12-31`) for
 * "never expires", so it is guarded here too rather than waiting to be
 * rediscovered when some listing grows an expiry column.
 */
const UNSET_DATE = /^(1969-12-31|2499-12-31)/;

/** A value is "empty" (row hidden) when null/undefined/''/[]/{} of no use. */
export function isEmptyValue(v) {
  if (v === null || v === undefined || v === '') return true;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === 'None' || t === 'null' || t === 'undefined') return true;
    if (UNSET_DATE.test(t)) return true;
  }
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') {
    // vocab term / relation summary with no useful label is empty
    const label = v.title || v.token || v['@id'] || v.data;
    return !label;
  }
  return false;
}

function fmtDate(v) {
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    // date-only strings parse as UTC midnight; format in UTC so the day
    // does not shift in western timezones (Dec 4 rendering as Dec 3)
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(v).trim());
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(dateOnly ? { timeZone: 'UTC' } : {}),
    });
  } catch {
    return String(v);
  }
}

/** A single relation/summary object -> a link to the target document. */
function relLink(item, key) {
  if (!item) return null;
  if (typeof item === 'string') return <span key={key}>{item}</span>;
  const label = item.title || item.token || item['@id'];
  const href = item['@id'] ? flattenToAppURL(item['@id']) : null;
  return href ? (
    <Link key={key} to={href} className="doc-relation-link">
      {label}
    </Link>
  ) : (
    <span key={key}>{label}</span>
  );
}

/**
 * Human status label/title (lowercased) -> CONTRACT A `.prc-pill` key.
 * Full-content status arrives as `{title, token}` where `token` is OPAQUE —
 * this map is only ever consulted with the human title/label, never the
 * token. Anything unrecognised falls back to `current`.
 */
const STATUS_KEY_MAP = {
  current: 'current',
  active: 'current',
  approved: 'current',
  'in effect': 'current',
  archive: 'archived',
  archived: 'archived',
  superseded: 'archived',
  expired: 'archived',
  inactive: 'archived',
  historical: 'archived',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  rescinded: 'rescinded',
  revoked: 'rescinded',
  private: 'private',
  draft: 'draft',
  pending: 'pending',
  'in review': 'pending',
  proposed: 'pending',
};

function statusKey(label) {
  return STATUS_KEY_MAP[String(label).trim().toLowerCase()] || 'current';
}

/**
 * Render a backend field value for the metadata table / relations panel.
 * Shape is inferred unless `format` forces date/relation/richtext/bool/status.
 */
export function renderValue(value, format = 'auto') {
  if (isEmptyValue(value)) return null;

  if (format === 'bool') return value ? 'Yes' : 'No';

  // status vocabulary term (string, or {token, title} summary) -> CONTRACT A
  // `.prc-pill`. Classification is always by the human title/label — the
  // `token` on a full-content status summary is opaque and never inspected.
  if (format === 'status') {
    const label = typeof value === 'string' ? value : value && (value.title || value.token);
    if (!label) return null;
    const key = statusKey(label);
    return (
      <span className={`prc-pill prc-pill--${key}`}>
        {key !== 'private' && <span className="prc-pill__dot" aria-hidden="true" />}
        {label}
      </span>
    );
  }

  if (format === 'richtext') {
    const html = value && value.data ? value.data : value;
    return (
      <span
        className="doc-richtext"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  if (format === 'date' && typeof value === 'string') return fmtDate(value);

  // relation: array of summary objects (or a single one) -> links
  if (format === 'relation') {
    const arr = Array.isArray(value) ? value : [value];
    const links = arr
      .filter((x) => !isEmptyValue(x))
      .map((x, i) => relLink(x, i));
    if (links.length === 0) return null;
    return (
      <span className="doc-relations">
        {links.map((l, i) => (
          <React.Fragment key={i}>
            {i > 0 ? ', ' : ''}
            {l}
          </React.Fragment>
        ))}
      </span>
    );
  }

  // --- auto shape inference ---
  if (typeof value === 'string') {
    // ISO-ish date string -> pretty date
    if (/^\d{4}-\d{2}-\d{2}([T ]|$)/.test(value)) return fmtDate(value);
    // migrated plain-Text fields (e.g. per-sense glossary definitions) carry
    // trusted archive HTML — render it, don't print markup
    if (/^\s*<[a-z][^>]*>/i.test(value)) return renderValue(value, 'richtext');
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    // relation-ish array (objects with @id) -> links; else joined labels
    if (value.some((x) => x && typeof x === 'object' && x['@id'])) {
      return renderValue(value, 'relation');
    }
    const parts = value
      .map((x) => (x && typeof x === 'object' ? x.title || x.token : x))
      .filter(Boolean);
    return parts.join(', ');
  }
  if (typeof value === 'object') {
    if (value['@id']) return renderValue(value, 'relation');
    if (value.data) return renderValue(value, 'richtext');
    return value.title || value.token || null;
  }
  return null;
}
